import { Component, inject, Inject, OnInit } from "@angular/core";
import { NavigationEnd, Router, RouterOutlet } from "@angular/router";
import { AuthService } from "@auth0/auth0-angular";
import { TranslocoService } from "@jsverse/transloco";
import { isEmpty } from "lodash";
import { pdfDefaultOptions } from "ngx-extended-pdf-viewer";
import { PrimeNGConfig as OldPrimeNGConfig } from "primeng-v17/api";
import { PrimeNG } from "primeng/config";
import { filter, map, switchMap, take } from "rxjs";
import { environment } from "../../environments/environment";
import { TermsAndConditionsService } from "../features/terms-and-conditions/terms-and-conditions.service";
import { UserEnvironmentService } from "../features/user-environment/user-environment.service";
import { NewUiGlobalConfirmationComponent } from "../shared/global-confirmation/new-ui-global-confirmation.component";
import { NewUiGlobalSpinnerComponent } from "../shared/global-spinner/new-ui-global-spinner.component";
import { NewUiGlobalToastComponent } from "../shared/global-toast/new-ui-global-toast.component";
import { ProgressDialogService } from "../shared/services/progress-dialog.service";
import { ProgressErrorNotifierService } from "../shared/services/progress-error-notifier.service";
import { ProgressNotificationService } from "../shared/services/progress-notification-service";
import { PromptUpdateService } from "../shared/services/promptUpdateService";
import { RouterHistoryService } from "../shared/services/route-stack.service";
import { SettingsService } from "../shared/services/settings.service";

@Component({
  selector: "new-ui-app",
  templateUrl: "./new-ui-app.component.html",
  host: { class: "block h-full w-full overflow-hidden" },
  imports: [NewUiGlobalSpinnerComponent, NewUiGlobalToastComponent, RouterOutlet, NewUiGlobalConfirmationComponent],
})
export class NewUiAppComponent implements OnInit {
  // Inject these services in order to make sure that they are initialized at the start of the app.
  protected progressNotificationService = inject(ProgressNotificationService);
  protected progressDialog = inject(ProgressDialogService);
  protected progressErrorNotifier = inject(ProgressErrorNotifierService);

  constructor(
    private readonly promptUpdateService: PromptUpdateService,
    @Inject(environment.newUI ? PrimeNG : OldPrimeNGConfig)
    private readonly primengConfig: PrimeNG | OldPrimeNGConfig,
    private readonly transloco: TranslocoService,
    private readonly authService: AuthService,
    private readonly routerStackService: RouterHistoryService,
    private readonly router: Router,
    private readonly settings: SettingsService,
    private readonly termsAndConditionsService: TermsAndConditionsService,
    private readonly userEnvironmentService: UserEnvironmentService
  ) {}

  ngOnInit(): void {
    // Chats and Consents should only be initialized once and after the initial consents has been showed
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map((event) => event.urlAfterRedirects),
        filter((url) => url.startsWith("/app")),
        switchMap(() => this.settings.isAuthorized$),
        filter((isAuthorized) => isAuthorized),
        take(1)
      )
      .subscribe(() => {
        const isNewUi = environment.newUI ?? false;
        const useNewUi = this.settings.isNewUiEnabled && this.settings.userPrefersNewUi;
        if (isNewUi !== useNewUi) {
          this.userEnvironmentService.navigateToAlternativeEnvironment();
          return;
        }

        this.navigateToConsentRouteInCaseConsentsExists();
      });

    this.routerStackService.init();

    this.promptUpdateService.checkForSoftwareUpdate();
    // AuthService must be here to be initialized before the app is loaded. Otherwise login fails
    this.authService.error$.subscribe((error) => {
      // eslint-disable-next-line no-console
      console.error("authService.error$", error);
    });

    if (!environment.newUI) {
      this.primengConfig.ripple = true;
    }

    this.primengConfig.setTranslation(this.transloco.translateObject("primeng"));

    pdfDefaultOptions.assetsFolder = "assets";
  }

  private navigateToConsentRouteInCaseConsentsExists(): void {
    this.termsAndConditionsService
      .getOutStandingConsents({
        consentGiverId: this.settings.userOrCandidateId,
        organizationId: this.settings.organizationId,
      })
      .subscribe((consents) => {
        if (!isEmpty(consents)) {
          void this.router.navigate(["consent", this.settings.organizationId, this.settings.userOrCandidateId], {
            state: { from: this.router.url },
          });
        }
      });
  }
}
